import type { ColumnsType } from 'antd/es/table'
import { PageLayout } from '@kolab/dms-base/components/layout'
import { exportDeviceInstallations, listDeviceInstallations } from '../../api/report.api'
import { reportKeys } from '../../api/queryKeys'
import type { DeviceInstallation } from '../../api/types'
import { ReportFilterBar } from '../../components/reports/ReportFilterBar'
import { ExportButton, ReportResults } from '../../components/reports/ReportResults'
import { useReportFilters } from '../../hooks/useReportFilters'
import { useReportPagination } from '../../hooks/useReportPagination'
import { Can, P } from '../../rbac'
import { formatInTz } from '../../utils/formatters'

/**
 * Day-wise Device Installation report: one row per device license activation
 * (first_activated_at), newest first. Filters live in the URL so dashboard
 * cards can deep-link a pre-filtered view.
 */
export default function DeviceInstallationReport() {
  const filters = useReportFilters()
  const pagination = useReportPagination<DeviceInstallation>({
    queryKey: (page) => reportKeys.installations.list({ ...filters.query, ...page }),
    queryFn: (page) => listDeviceInstallations({ ...filters.query, ...page }),
    pageSize: filters.values.pageSize,
    resetKey: filters.resetKey,
    enabled: !!filters.query,
  })
  const tz = pagination.timezone

  const columns: ColumnsType<DeviceInstallation> = [
    { title: 'Date', dataIndex: 'installedDate', key: 'date', width: 120 },
    { title: 'Device ID / Serial', dataIndex: 'deviceId', key: 'deviceId', ellipsis: true },
    { title: 'Device Name', dataIndex: 'deviceName', key: 'deviceName', ellipsis: true, render: dash },
    {
      title: 'Installation Date & Time',
      dataIndex: 'installedAt',
      key: 'installedAt',
      width: 190,
      render: (v: string) => formatInTz(v, tz),
    },
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', ellipsis: true, render: dash },
  ]

  return (
    <PageLayout
      filters={
        <ReportFilterBar
          values={filters.values}
          error={filters.error}
          timezone={tz}
          hasActiveFilters={filters.hasActiveFilters}
          onChange={filters.setFilters}
          onReset={filters.reset}
        />
      }
      actions={
        <Can permission={P.reports.device_installation.export}>
          <ExportButton
            disabled={!filters.query || pagination.totalCount === 0}
            onExport={() => exportDeviceInstallations(filters.query!)}
          />
        </Can>
      }
    >
      <ReportResults<DeviceInstallation>
        pagination={pagination}
        columns={columns}
        rowKey="id"
        values={filters.values}
        onSortChange={filters.setFilters}
        onPageSizeChange={filters.setPageSize}
        blocked={!filters.query}
      />
    </PageLayout>
  )
}

function dash(v: string) {
  return v || '—'
}
